import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Renders a circular user avatar. Falls back to a coloured circle with the
 * user's initials (first letter of firstname + first letter of lastname) when
 * no image URL is provided.
 *
 * Used in:
 *   - hypecast-header (~32 px)
 *   - profile page (~80 px)
 */
@Component({
  selector: 'hypecast-user-avatar',
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAvatarComponent {
  /** Optional image URL. When set, an <img> is rendered instead of initials. */
  readonly imageUrl = input<string | null | undefined>();
  readonly firstname = input<string | null | undefined>();
  readonly lastname = input<string | null | undefined>();

  /** CSS size value (e.g. "32px", "80px"). Drives width, height and font-size. */
  readonly size = input('40px');

  /** Hint for the <img> alt attribute and the initials container aria-label. */
  readonly altText = input('User avatar');

  readonly initials = computed(() => {
    const first = this.firstChar(this.firstname());
    const last = this.firstChar(this.lastname());
    return `${first}${last}` || '?';
  });

  /** Font-size derived from size so initials always read at ~40% of the circle. */
  readonly fontSize = computed(() => {
    const px = parseFloat(this.size());
    if (Number.isFinite(px) && px > 0) {
      return `${Math.max(10, Math.round(px * 0.4))}px`;
    }
    return '1rem';
  });

  private firstChar(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    // Use Array.from to handle astral characters (emoji etc) gracefully.
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    return Array.from(trimmed)[0].toLocaleUpperCase();
  }
}
